import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { GiftCard } from '@nucleus-commerce/api-client/admin';
import { isApiError } from '@nucleus-commerce/api-client/transport';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import { Label } from '@nucleus-commerce/ui/components/label';

import { CustomerPicker } from '@/features/customers';

import { useUpdateGiftCard } from '../hooks/use-gift-card-mutations';

export interface ReassignCustomerDialogProps {
  storeId: string;
  card: GiftCard;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function ReassignForm({
  storeId,
  card,
  onDone,
}: {
  storeId: string;
  card: GiftCard;
  onDone: () => void;
}) {
  const { t } = useTranslation(['giftCards', 'common', 'errors']);
  const update = useUpdateGiftCard(storeId, 'giftCards:toasts.reassigned');
  const message = useErrorMessage();
  const [customerId, setCustomerId] = useState<string | null>(card.customerId);
  const requestId = isApiError(update.error) ? update.error.requestId : undefined;

  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        update.mutate({ id: card.id, body: { customerId } }, { onSuccess: onDone });
      }}
    >
      {update.isError ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription>
            {message(update.error)}
            {requestId ? (
              <span className="block text-xs">{t('errors:requestIdShort', { requestId })}</span>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}
      <div className="flex flex-col gap-2">
        <Label htmlFor="gift-card-customer">{t('giftCards:fields.customer')}</Label>
        <CustomerPicker
          id="gift-card-customer"
          storeId={storeId}
          value={customerId}
          onChange={(customer) => {
            update.reset();
            setCustomerId(customer?.id ?? null);
          }}
          aria-invalid={update.isError}
        />
        <p className="text-muted-foreground text-xs">{t('giftCards:reassign.hint')}</p>
      </div>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common:actions.cancel')}
        </Button>
        <Button type="submit" disabled={update.isPending || customerId === card.customerId}>
          {update.isPending ? t('common:actions.saving') : t('common:actions.save')}
        </Button>
      </DialogFooter>
    </form>
  );
}

/** Moves the card to another customer of the store, or detaches it. */
export function ReassignCustomerDialog({
  storeId,
  card,
  open,
  onOpenChange,
}: ReassignCustomerDialogProps) {
  const { t } = useTranslation('giftCards');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('reassign.title')}</DialogTitle>
          <DialogDescription>{t('reassign.description')}</DialogDescription>
        </DialogHeader>
        {open ? (
          <ReassignForm storeId={storeId} card={card} onDone={() => onOpenChange(false)} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
